import Add from "@mui/icons-material/Add";
import { Button } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import {
  StateWasteStreamRegistrationsQuery,
  StateWasteStreamRegistrationsQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCallback, useMemo, useState } from "react";

import { STATE_WASTE_STREAM_REGISTRATIONS_QUERY } from "./api";
import { COLUMNS, EXCLUDE_FILTER_COLUMNS, RegistrationRow } from "./columns";
import { CreateRegistrationDialog } from "./CreateRegistrationDialog";
import { EditRegistrationDrawer } from "./EditRegistrationDrawer";

// Stable, module-level grid config (V2 requires stable references for its
// perf model).
function getItems(data: StateWasteStreamRegistrationsQuery): RegistrationRow[] {
  return data.stateWasteStreamRegistrations?.items ?? [];
}

function getCount(data: StateWasteStreamRegistrationsQuery): number {
  return data.stateWasteStreamRegistrations?.count ?? 0;
}

function getRowId(row: RegistrationRow): string {
  return row.id;
}

function buildVariables(
  search: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
): StateWasteStreamRegistrationsQueryVariables {
  return {
    search,
    page: pagination.page,
    pageSize: pagination.pageSize,
    sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
  };
}

const INITIAL_SORT_MODEL: GridSortModel = [{ field: "state", sort: "asc" }];

// noLinkedStream is filter-only: the "No linked Encamp stream" filter needs the
// column defined (filters are derived from `columns`), but the Linked Streams
// count already conveys the same thing, so the column itself stays hidden.
const COLUMN_VISIBILITY_MODEL = { noLinkedStream: false };

export const StateWasteStreams = () => {
  useBreadcrumb({ label: "State Waste Streams" });

  const [selectedRegistration, setSelectedRegistration] = useState<RegistrationRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const onRowClick = useCallback(
    (params: GridRowParams<RegistrationRow>) => setSelectedRegistration(params.row),
    []
  );

  const commandButtons = useMemo(
    () => [
      <Button
        key="create-registration"
        variant="contained"
        startIcon={<Add />}
        onClick={() => setCreateOpen(true)}
      >
        Create registration
      </Button>,
    ],
    []
  );

  return (
    <>
      <OmnisearchDataGridV2<
        StateWasteStreamRegistrationsQuery,
        StateWasteStreamRegistrationsQueryVariables,
        RegistrationRow
      >
        query={STATE_WASTE_STREAM_REGISTRATIONS_QUERY}
        persistenceKey="staff-state-waste-stream-registrations"
        columns={COLUMNS}
        getItems={getItems}
        getCount={getCount}
        getRowId={getRowId}
        buildVariables={buildVariables}
        initialPageSize={25}
        initialSortModel={INITIAL_SORT_MODEL}
        initialColumnVisibilityModel={COLUMN_VISIBILITY_MODEL}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        noRowsMessage="No state waste stream registrations found."
        commandButtons={commandButtons}
        onRowClick={onRowClick}
        sx={{ pt: 3 }}
      />

      <EditRegistrationDrawer
        registration={selectedRegistration}
        onClose={() => setSelectedRegistration(null)}
      />
      <CreateRegistrationDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
};
